/* eslint-disable no-case-declarations */
import { action, observable } from 'mobx';
import { applyPatches } from 'mobx-keystone';
import { customAlphabet } from 'nanoid/non-secure';

import type {
  GameState,
  GameStatePatchWithVersion,
  GameVersionMetadataPayload,
  PlayerData,
  PlayP2pMessage,
  PlayServerDisconnectReason,
  PlayUserType,
  PlayWebsocketClientToServerGameStarted,
  PlayWebsocketClientToServerMessage,
  PlayWebsocketServerToClientMessage,
} from '@genially/p2p-lib';
import {
  BaseErrorCodes,
  isPlayP2pMessage,
  isValidPlayErrorCode,
  isValidPlayServerDisconnectReason,
  PlayErrorCodes,
  PlayWebsocketClientToServerMessageType,
} from '@genially/p2p-lib';
import type { FEATURE_FLAGS } from '@genially/ui';
import { decodeError } from '@genially/ui/build/utils/decodeError';
import { MeasurePerformanceService } from '@genially/utils';

import { env } from '../../config/env';
import type { P2pLogger } from '../../util/logger';
import { ProcessingQueue } from '../../util/ProcessingQueue';
import type { P2pClientEmittedEvents, P2pConstructorParams } from '../P2pClient';
import { P2pClient } from '../P2pClient';
import { FakeGameCompressor } from './implementations/utils/FakeGameCompressor';
import { GameCompressor } from './implementations/utils/GameCompressor';
import { GameConfigDebugConfig } from './implementations/utils/GameConfigDebugConfig';
import type { GameStateModel } from './models/GameStateModel';
import { createGameStateModelFromType } from './models/GameStateModel';

export type PlayClientConstructorParams = Omit<
  P2pConstructorParams,
  'connectionPath' | 'targetId' | 'cookie' | 'roomId'
> &
  (
    | {
        geniallyId: string;
        type: PlayUserType.Host;
        game: GameState;
        deviceId: string;
        sessionId: string;
        featureFlags: string[];
        playersLimitPerSession: number;
      }
    | {
        geniallyId: string;
        type: PlayUserType.Player;
        game: GameState;
        deviceId: string;
        sessionId: string;
        username: string;
        featureFlags: string[];
      }
  );

export enum PlayP2pClientEventType {
  Error = 'error',
  Dispose = 'dispose',
}

export type PlayP2pClientEmittedEvents<
  T extends { [k: string]: Array<any> } = Record<string, any>,
> = P2pClientEmittedEvents<{
  [PlayP2pClientEventType.Error]: [BaseErrorCodes | PlayErrorCodes];
  [PlayP2pClientEventType.Dispose]: [void];
}> & {
  [k in keyof T]: T[k];
};

export const SessionIdLength = 6;

export abstract class PlayP2pClient<
  EmittedEvents extends PlayP2pClientEmittedEvents = PlayP2pClientEmittedEvents,
  PlayOnPeerConnectionMetadata = unknown,
> extends P2pClient<
  PlayWebsocketServerToClientMessage,
  PlayWebsocketClientToServerMessage,
  PlayP2pMessage,
  PlayErrorCodes,
  PlayServerDisconnectReason,
  PlayOnPeerConnectionMetadata,
  EmittedEvents
> {
  protected readonly processingQueue = new ProcessingQueue();

  protected readonly measurePerformanceService = new MeasurePerformanceService(
    'PlayP2pClient',
  );

  protected readonly type: PlayUserType;

  protected abstract readonly _logger: P2pLogger;

  @observable
  protected _game: GameStateModel;

  public readonly deviceId: string;

  public readonly sessionId: string;

  public readonly geniallyId: string;

  protected gameCompressor: GameCompressor;

  @observable
  protected featureFlags: string[];

  private lastGameVersionNotifiedToServer: number = 0;

  private gameVersionNotificationIntervalPid: number | undefined = undefined;

  protected disposers: (() => void)[] = [];

  protected constructor(params: PlayClientConstructorParams) {
    const { type, sessionId, geniallyId, deviceId, game, featureFlags } = params;

    super({
      roomId: sessionId,
      targetId: geniallyId,
      connectionPath: `${env.p2pDefaultPath}/play`,
    });

    this.type = type;
    this._game = createGameStateModelFromType(game);
    this.geniallyId = geniallyId;
    this.deviceId = deviceId;
    this.sessionId = sessionId;
    this.featureFlags = featureFlags;

    const avoidGameCompression = GameConfigDebugConfig.skipCompress;

    if (avoidGameCompression) {
      this.gameCompressor = new FakeGameCompressor({
        measurePerformanceService: this.measurePerformanceService,
      });
    } else {
      this.gameCompressor = new GameCompressor({
        measurePerformanceService: this.measurePerformanceService,
      });
    }

    this.gameVersionNotificationIntervalPid = window.setInterval(
      this.notifyToServerCurrentVersion.bind(this),
      10000,
    );
  }

  private notifyToServerCurrentVersion() {
    if (this.lastGameVersionNotifiedToServer === this._game.version) {
      return;
    }

    if (!this.isReadyToSendMessages) {
      return;
    }

    const message: PlayWebsocketClientToServerMessage = {
      type: PlayWebsocketClientToServerMessageType.SetCurrentGameVersion,
      version: this._game.version,
    };

    this.sendWebsocketMessage(message);

    this.lastGameVersionNotifiedToServer = this._game.version;
  }

  buildTransportParams(): Record<string, string> {
    return {
      geniallyId: this.geniallyId,
      deviceId: this.deviceId,
      sessionId: this.sessionId,
      type: this.type,
      gameVersion: this._game.version.toString(),
    };
  }

  get game() {
    return this._game;
  }

  @action
  protected setGame(game: GameState) {
    const gameWillStart = !this._game.isStarted && game.isStarted;

    this._game = createGameStateModelFromType(game);

    if (gameWillStart) {
      this.sendGameStartWebsocketMessage();
    }
  }

  @action
  protected applyGamePatches(gameStatePatchWithVersion: GameStatePatchWithVersion) {
    const { version, patches } = gameStatePatchWithVersion;

    if (version !== this._game.version + 1) {
      this._logger.warn(
        `Received patch with version ${version} but current version is ${this._game.version}`,
      );

      throw new Error(
        `Received patch with version ${version} but current version is ${this._game.version}`,
      );
    }

    try {
      applyPatches(this.game, patches);
    } catch (error) {
      this._logger.error('Error applying patches', { error: decodeError(error) });

      throw new Error('Error applying patches');
    }
  }

  abstract get players(): PlayerData[];

  protected sendGameStartWebsocketMessage() {
    const message: PlayWebsocketClientToServerGameStarted = {
      type: PlayWebsocketClientToServerMessageType.GameStarted,
      startedAt: this.game.startedAt!,
    };

    this.sendWebsocketMessage(message);
  }

  isValidWebsocketMessage(_: unknown): _ is PlayWebsocketServerToClientMessage {
    // We don't need to handle messages from the server
    return false;
  }

  protected static generateDeviceId() {
    return customAlphabet(
      '1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMOPQRSTUVWXYZ',
      20,
    )();
  }

  protected static generateSessionId() {
    return customAlphabet('1234567890', SessionIdLength)();
  }

  public static isValidSessionId(sessionId: unknown): sessionId is string {
    return typeof sessionId === 'string' && sessionId.length === SessionIdLength;
  }

  public static isValidGeniallyId(geniallyId: unknown): geniallyId is string {
    return typeof geniallyId === 'string' && geniallyId.length === 24;
  }

  public static isValidTeamId(teamId: unknown): teamId is string {
    return typeof teamId === 'string' && teamId.length === 24;
  }

  public static isValidUsername(username: unknown) {
    return (
      typeof username === 'string' &&
      username.length > 0 &&
      username.trim().length === username.length
    );
  }

  isValidP2pMessage(message: unknown): message is PlayP2pMessage {
    return isPlayP2pMessage(message);
  }

  protected onServerConnection() {
    super.onServerConnection();

    if (this.game.isStarted) {
      this.sendGameStartWebsocketMessage();
    }
  }

  protected onServerError(code: PlayErrorCodes | BaseErrorCodes): { handled: boolean } {
    this.emitter.emit(PlayP2pClientEventType.Error, code);

    switch (code) {
      case BaseErrorCodes.InvalidAuthorizationRequest:
      case BaseErrorCodes.Unauthorized:
      case PlayErrorCodes.NoParamsFound:
      case PlayErrorCodes.InvalidDeviceId:
      case PlayErrorCodes.InvalidGeniallyUserId:
      case PlayErrorCodes.InvalidTeamId:
      case PlayErrorCodes.InvalidUsername:
      case PlayErrorCodes.InvalidGeniallyId:
      case PlayErrorCodes.InvalidType:
      case PlayErrorCodes.InvalidSessionId:
      case PlayErrorCodes.InvalidGameVersion:
      case PlayErrorCodes.InvalidCookie:
      case PlayErrorCodes.CannotReplaceHost:
      case PlayErrorCodes.SessionIdInUse:
      case PlayErrorCodes.UsernameInUse:
        this._logger.error(`Client disposed due to an error: ${code}`);

        this.dispose();

        return { handled: true };
      default:
        return { handled: false };
    }
  }

  protected isValidWebsocketServerError(code: unknown): code is PlayErrorCodes {
    return isValidPlayErrorCode(code);
  }

  protected onServerDisconnection(_: PlayServerDisconnectReason) {
    // Implement in subclass if needed
  }

  protected isValidServerDisconnectReason(
    reason: unknown,
  ): reason is PlayServerDisconnectReason {
    return isValidPlayServerDisconnectReason(reason);
  }

  dispose() {
    this.disposers.forEach(disposer => disposer());

    this.emitter.emit(PlayP2pClientEventType.Dispose);

    super.dispose();

    if (this.gameVersionNotificationIntervalPid) {
      clearInterval(this.gameVersionNotificationIntervalPid);
    }

    this.game.dispose();

    this._logger.debug('PlayP2pClient disposed');
  }

  protected appendGameVersionToMessage(
    message: Omit<PlayP2pMessage, 'gameVersion'>,
  ): GameVersionMetadataPayload<PlayP2pMessage> {
    this.lastGameVersionNotifiedToServer = this._game.version;

    return {
      ...message,
      gameVersion: this._game.version,
    } as PlayP2pMessage;
  }

  public hasFeatureFlag(featureFlag: FEATURE_FLAGS): boolean {
    return this.featureFlags.includes(featureFlag);
  }

  @action
  protected setFeatureFlags(featureFlags: string[]): void {
    this.featureFlags = featureFlags;
  }
}
